import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AdminAPI } from '@shared/types/api';
import {api} from '@/lib/api';

/**
 * Filters shared by the feedback list and the stats chart. Dates are
 * inclusive YYYY-MM-DD strings; `eventType: 'all'` or an omitted field means
 * no restriction. The server filters the list by submission date and the
 * chart by event date.
 */
export interface FeedbackFilters {
  eventType?: AdminAPI.FeedbackEventType | 'all';
  startDate?: string;
  endDate?: string;
}

export function buildFeedbackQuery(filters: FeedbackFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.eventType && filters.eventType !== 'all') {
    params.set('eventType', filters.eventType);
  }
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useAdminFeedback(filters: FeedbackFilters = {}) {
  const query = buildFeedbackQuery(filters);
  return useQuery<AdminAPI.GetFeedbackResponse>({
    queryKey: ['admin', 'feedback', 'list', query],
    queryFn: async () => {
      return await api.get(`/admin/feedback${query}`);
    },
    // Keep the current list on screen while a filter change refetches
    placeholderData: keepPreviousData,
  });
}

export function useAdminFeedbackStats(filters: FeedbackFilters = {}) {
  const query = buildFeedbackQuery(filters);
  return useQuery<AdminAPI.GetFeedbackStatsResponse>({
    queryKey: ['admin', 'feedback', 'stats', query],
    queryFn: async () => {
      return await api.get(`/admin/feedback/stats${query}`);
    },
    placeholderData: keepPreviousData,
  });
}
